import { useContext, useEffect, useState } from "react";
import api from "../api/client";
import AuthContext from "../context/AuthContext";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useScreen } from "../hooks/useScreen";

const emptyForm = {
  operation_date: "",
  camera_id: "",
  expected_count: "",
  lote_number: "",
  trip_number: "",
  notes: "",
};

const statusLabels = {
  pending: "Pendiente",
  matched: "Coincide",
  under: "Faltante",
  over: "Excedido",
};

export default function Previsiones() {
  const { user } = useContext(AuthContext);
  const { isXs } = useScreen();
  const [previsions, setPrevisions] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedPrevision, setSelectedPrevision] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const isAdmin = Boolean(user?.is_admin);

  const normalizeError = (detail, fallback) => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(" | ");
    }
    if (detail && typeof detail === "object") {
      return detail.message || JSON.stringify(detail);
    }
    return fallback;
  };

  const showError = (detail, fallback) => setError(normalizeError(detail, fallback));

  const cameraLabelById = Object.fromEntries(
    cameras.map((camera) => [
      camera.id,
      [camera.location, camera.nave].filter(Boolean).join(" - ") || `Cámara ${camera.id}`,
    ])
  );

  const fetchCameras = () => {
    api
      .get("/cameras/")
      .then((res) => setCameras(res.data))
      .catch((err) => showError(err?.response?.data?.detail, "No se pudieron cargar las cámaras"));
  };

  const fetchPrevisions = () => {
    api
      .get("/previsions/")
      .then(async (res) => {
        const rows = await Promise.all(
          res.data.map(async (prevision) => {
            try {
              const comparisonResponse = await api.get(`/previsions/${prevision.id}/comparison`);
              return { ...prevision, comparison: comparisonResponse.data };
            } catch (comparisonError) {
              return {
                ...prevision,
                comparison: {
                  actual_ai_count: 0,
                  actual_supervised_count: 0,
                  difference_ai: 0,
                  difference_supervised: 0,
                  status: "pending",
                  has_pending_registries: true,
                },
              };
            }
          })
        );

        setPrevisions(rows);
      })
      .catch((err) => showError(err?.response?.data?.detail, "No se pudieron cargar las previsiones"));
  };

  useEffect(() => {
    fetchCameras();
    fetchPrevisions();
  }, []);

  const handleOpenCreate = () => {
    setEditing(false);
    setSelectedPrevision(null);
    setFormData(emptyForm);
    setOpen(true);
  };

  const handleOpenEdit = (prevision) => {
    setEditing(true);
    setSelectedPrevision(prevision);
    setFormData({
      operation_date: prevision.operation_date,
      camera_id: String(prevision.camera_id),
      expected_count: String(prevision.expected_count),
      lote_number: prevision.lote_number || "",
      trip_number: prevision.trip_number || "",
      notes: prevision.notes || "",
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(false);
    setSelectedPrevision(null);
    setFormData(emptyForm);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const buildPayload = () => ({
    operation_date: formData.operation_date,
    camera_id: Number(formData.camera_id),
    expected_count: Number(formData.expected_count),
    lote_number: formData.lote_number || null,
    trip_number: formData.trip_number || null,
    notes: formData.notes || null,
  });

  const handleSave = () => {
    const payload = buildPayload();
    const request = editing
      ? api.put(`/previsions/${selectedPrevision.id}`, payload)
      : api.post("/previsions/", payload);

    request
      .then(() => {
        setSuccessMessage(editing ? "Previsión actualizada correctamente" : "Previsión creada correctamente");
        handleClose();
        fetchPrevisions();
      })
      .catch((err) => showError(err?.response?.data?.detail, "No se pudo guardar la previsión"));
  };

  const handleDelete = (id) => {
    if (!window.confirm("¿Seguro que querés eliminar esta previsión?")) return;

    api
      .delete(`/previsions/${id}`)
      .then(() => {
        setSuccessMessage("Previsión eliminada correctamente");
        fetchPrevisions();
      })
      .catch((err) => showError(err?.response?.data?.detail, "No se pudo eliminar la previsión"));
  };

  const columns = [
    { field: "operation_date", headerName: "Fecha", flex: 0.9 },
    {
      field: "camera_name",
      headerName: "Cámara",
      flex: 1.4,
      valueGetter: (_, row) => cameraLabelById[row.camera_id] || `Cámara ${row.camera_id}`,
    },
    {
      field: "lote_number",
      headerName: "Lote",
      flex: 0.8,
      valueGetter: (_, row) => row.lote_number || "-",
    },
    {
      field: "trip_number",
      headerName: "Viaje",
      flex: 0.8,
      valueGetter: (_, row) => row.trip_number || "-",
    },
    { field: "expected_count", headerName: "Previsto", flex: 0.8, type: "number" },
    {
      field: "actual_ai_count",
      headerName: "Real IA",
      flex: 0.8,
      type: "number",
      valueGetter: (_, row) => row.comparison?.actual_ai_count ?? 0,
    },
    {
      field: "actual_supervised_count",
      headerName: "Real supervisado",
      flex: 1,
      type: "number",
      valueGetter: (_, row) => row.comparison?.actual_supervised_count ?? 0,
    },
    {
      field: "difference_ai",
      headerName: "Diferencia",
      flex: 0.8,
      type: "number",
      valueGetter: (_, row) => row.comparison?.difference_ai ?? 0,
    },
    {
      field: "status",
      headerName: "Estado",
      flex: 0.8,
      valueGetter: (_, row) => statusLabels[row.comparison?.status] || row.comparison?.status || "Pendiente",
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 1.4,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, py: 1, flexWrap: "wrap" }}>
          {isAdmin ? (
            <>
              <Button size="small" color="warning" onClick={() => handleOpenEdit(params.row)}>
                Editar
              </Button>
              <Button size="small" color="error" onClick={() => handleDelete(params.row.id)}>
                Eliminar
              </Button>
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Solo lectura
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Previsiones
      </Typography>

      {isAdmin ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button variant="contained" onClick={handleOpenCreate}>
            Agregar previsión
          </Button>
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Solo un administrador puede crear, editar o eliminar previsiones.
        </Alert>
      )}

      <Box sx={{ width: "100%", height: isXs ? 420 : 560 }}>
        <DataGrid
          rows={previsions}
          columns={columns}
          disableColumnMenu={isXs}
          density={isXs ? "compact" : "standard"}
          initialState={{ pagination: { paginationModel: { pageSize: isXs ? 5 : 10 } } }}
          pageSizeOptions={[5, 10, 20]}
          disableRowSelectionOnClick
        />
      </Box>

      <Dialog open={open} onClose={handleClose} fullScreen={isXs} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Editar previsión" : "Agregar previsión"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Fecha"
              name="operation_date"
              type="date"
              value={formData.operation_date}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              select
              label="Cámara"
              name="camera_id"
              value={formData.camera_id}
              onChange={handleChange}
              fullWidth
            >
              {cameras.map((camera) => (
                <MenuItem key={camera.id} value={String(camera.id)}>
                  {cameraLabelById[camera.id] || `Cámara ${camera.id}`}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Cantidad prevista"
              name="expected_count"
              type="number"
              value={formData.expected_count}
              onChange={handleChange}
              fullWidth
              inputProps={{ min: 1 }}
            />

            <TextField label="Lote" name="lote_number" value={formData.lote_number} onChange={handleChange} fullWidth />
            <TextField label="Viaje" name="trip_number" value={formData.trip_number} onChange={handleChange} fullWidth />
            <TextField label="Notas" name="notes" value={formData.notes} onChange={handleChange} fullWidth multiline minRows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(successMessage)} autoHideDuration={3000} onClose={() => setSuccessMessage("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSuccessMessage("")} severity="success" variant="filled" sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar open={Boolean(error)} autoHideDuration={4000} onClose={() => setError("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setError("")} severity="error" variant="filled" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
